import { formatISO } from 'date-fns'
import {
  EHealthProceduresConducts,
  EHealthProceduresOmics,
  EHealthProceduresSubtypes,
  EHealthProceduresTypes,
  HealthProceduresTypesConduct,
  HealthProceduresTypesOmics,
  IHealthProcedures,
  IHealthProceduresConduct,
  IHealthProceduresOmics,
  ITimelineFiltersFunctor,
  IUrineCultureFiltersFunctor,
  IUrineCultureTest
} from '~/interfaces'

export const isOmics = (healthProcedure: IHealthProcedures): healthProcedure is IHealthProceduresOmics => {
  const omicsTypes: HealthProceduresTypesOmics[] = Object.values(EHealthProceduresOmics)
  return omicsTypes.includes(healthProcedure.type as HealthProceduresTypesOmics)
}

export const isConduct = (healthProcedure: IHealthProcedures): healthProcedure is IHealthProceduresConduct => {
  const conductTypes: HealthProceduresTypesConduct[] = Object.values(EHealthProceduresConducts)
  return conductTypes.includes(healthProcedure.type as HealthProceduresTypesConduct)
}

export const byTypeExceptDiagnosis = (healthProcedure: IHealthProcedures): boolean =>
  healthProcedure.type !== EHealthProceduresTypes.DIAGNOSIS

export const byTypeExceptAdmission = (healthProcedure: IHealthProcedures): boolean =>
  healthProcedure.type !== EHealthProceduresTypes.ADMISSION

export const byTypeEqualToAdmission = (healthProcedure: IHealthProcedures): boolean =>
  healthProcedure.type === EHealthProceduresTypes.ADMISSION

export const byTypeEqualToDiagnosis = (healthProcedure: IHealthProcedures): boolean =>
  healthProcedure.type === EHealthProceduresTypes.DIAGNOSIS

export const filterHealthProceduresPer = <T>(
  healthProcedures: T[],
  callbackFilter: (healthProcedure: T) => boolean
): T[] => {
  return healthProcedures.filter(callbackFilter)
}

interface IRequiredFieldsToFilterByDates {
  startDate: string
  endDate: string
}

const filterPerRangeOfDates = <T extends IRequiredFieldsToFilterByDates>(
  rangeDates: Date[],
  healthProcedures: T[]
): T[] => {
  const hasNotDatesSelected = rangeDates.length === 0
  if (hasNotDatesSelected) {
    return healthProcedures
  }
  const [startDateSelected, endDateSelected] = rangeDates.map(date => new Date(date).getTime())
  const healthProceduresInPeriodOfTime = healthProcedures.filter(healthProcedure => {
    const healthProcedureStartDate = new Date(healthProcedure.startDate).getTime()
    const healthProcedureEndDate = new Date(healthProcedure.endDate).getTime()
    return healthProcedureStartDate >= startDateSelected && healthProcedureEndDate <= endDateSelected
  })

  return healthProceduresInPeriodOfTime
}

const filterPerStartDateEventBetweenDates = <T extends IRequiredFieldsToFilterByDates>(
  rangeDates: Date[],
  healthProcedures: T[]
): T[] => {
  const hasNotDatesSelected = rangeDates.length === 0
  if (hasNotDatesSelected) {
    return healthProcedures
  }
  const [startDateSelected, endDateSelected] = rangeDates.map(date => new Date(date).getTime())
  const healthProceduresInPeriodOfTime = healthProcedures.filter(healthProcedure => {
    const healthProcedureStartDate = new Date(healthProcedure.startDate).getTime()
    return healthProcedureStartDate >= startDateSelected && healthProcedureStartDate <= endDateSelected
  })

  return healthProceduresInPeriodOfTime
}

const filterPerEndDateEventBetweenDates = <T extends IRequiredFieldsToFilterByDates>(
  rangeDates: Date[],
  healthProcedures: T[]
): T[] => {
  const hasNotDatesSelected = rangeDates.length === 0
  if (hasNotDatesSelected) {
    return healthProcedures
  }
  const [startDateSelected, endDateSelected] = rangeDates.map(date => new Date(date).getTime())
  const healthProceduresInPeriodOfTime = healthProcedures.filter(healthProcedure => {
    const healthProcedureEndDate = new Date(healthProcedure.endDate).getTime()
    return healthProcedureEndDate >= startDateSelected && healthProcedureEndDate <= endDateSelected
  })

  return healthProceduresInPeriodOfTime
}

const filterHealthProceduresPerType = (
  typeFilters: string[],
  healthProcedures: IHealthProcedures[]
): IHealthProcedures[] => {
  const isTypeSelectedEmpty = typeFilters.length === 0
  if (isTypeSelectedEmpty) {
    return healthProcedures
  }

  const healthProceduresOfTypeSelected = healthProcedures.filter(healthProcedure => {
    const hasEqualType = typeFilters.includes(healthProcedure.type)

    const hasEqualSubType = typeFilters.some(filterActives => {
      const [healthProcedureType, healthProcedureSubTypeName] = filterActives.split('-')
      if (healthProcedureSubTypeName && healthProcedureType === healthProcedure.type) {
        const healthProcedureSubTypeNumber = EHealthProceduresSubtypes[healthProcedureSubTypeName]
        return healthProcedure.subtypes.includes(healthProcedureSubTypeNumber)
      }
    })
    return hasEqualType || hasEqualSubType
  })

  return healthProceduresOfTypeSelected
}

const filterHealthProceduresPerTerm = (term: string | undefined, healthProcedures: IHealthProcedures[]) => {
  if (!term) return healthProcedures
  const healthProceduresFiltered = healthProcedures.filter(option =>
    option.description.toUpperCase().includes(term.toUpperCase())
  )
  return healthProceduresFiltered
}

const filterHealthProceduresPerMultipleTextTerms = (
  terms: string[] | undefined,
  healthProcedures: IHealthProcedures[]
) => {
  if (!terms) return healthProcedures
  if (terms && !terms.length) return healthProcedures

  const removeAccents = (str: string) => {
    return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  }

  const healthProceduresFiltered = healthProcedures.filter(option => {
    const description = removeAccents(option.description.toUpperCase())
    return terms.some(term => description.includes(removeAccents(term.toUpperCase())))
  })
  return healthProceduresFiltered
}

// FUNCTOR'S
export const timelineFilters = (healthProcedures: IHealthProcedures[]): ITimelineFiltersFunctor => ({
  value: healthProcedures,
  perDate: dates => timelineFilters(filterPerRangeOfDates(dates, healthProcedures)),
  perEventStartDate: dates => timelineFilters(filterPerStartDateEventBetweenDates(dates, healthProcedures)),
  perEventEndDate: dates => timelineFilters(filterPerEndDateEventBetweenDates(dates, healthProcedures)),
  perType: (types: string[]) => timelineFilters(filterHealthProceduresPerType(types, healthProcedures)),
  perTextTerm: (term: string) => timelineFilters(filterHealthProceduresPerTerm(term, healthProcedures)),
  perMultipleTextTerms: (terms: string[]) =>
    timelineFilters(filterHealthProceduresPerMultipleTextTerms(terms, healthProcedures))
})

export const urineCultureFilters = (urineCultureTests: IUrineCultureTest[]): IUrineCultureFiltersFunctor => ({
  value: urineCultureTests,
  perDate: (dates: Date[]) => urineCultureFilters(filterPerRangeOfDates(dates, urineCultureTests))
})

export const identifyInProgressEvents = (healthProcedures: IHealthProcedures[]): IHealthProcedures[] => {
  const healthProceduresInProgress = healthProcedures.map(healthProcedure => {
    const isInProgress = !healthProcedure.endDate
    if (isInProgress) {
      return {
        ...healthProcedure,
        isInProgress,
        endDate: formatISO(new Date())
      }
    }
    return healthProcedure
  })
  return healthProceduresInProgress
}

export * from './timeline'
export const monthsNames = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez']
